import { format } from 'date-fns';
import { ClientError } from 'graphql-request';
import {
  delay,
  EMPTY,
  filter,
  forkJoin,
  from,
  map,
  Observable,
  of,
  repeat,
  retry,
  throwIfEmpty,
  switchMap,
  takeWhile,
  finalize,
  zipWith,
  take,
  tap,
  zip,
} from 'rxjs';
import {
  C_Report_Error_Code,
  C_Email_Msg_Doctype,
  C_History_Pdf_Doctype,
  C_Report,
  C_Win_Report_Task_Status,
  Create_R3_CustomerAddressesListReportMutation,
  Create_R3_CustomerAddressesListReportMutationVariables,
  Create_R4_CustomerFactSheetReportMutation,
  Create_R4_CustomerFactSheetReportMutationVariables,
  Create_R5_CustomerPhonesListReportMutation,
  Create_R5_CustomerPhonesListReportMutationVariables,
  Create_R7_WeeklyOrderingListMutation,
  Create_R7_WeeklyOrderingListMutationVariables,
  Create_R33_PriceListForCustomerMutation,
  Create_R33_PriceListForCustomerMutationVariables,
  Create_R28_WeeklyOrderingList_Blank_QuantityMutation,
  Create_R28_WeeklyOrderingList_Blank_QuantityMutationVariables,
  Create_R47_WeeklyOrderingList_Blank_Quantity_Blank_DateMutation,
  Create_R47_WeeklyOrderingList_Blank_Quantity_Blank_DateMutationVariables,
  Create_R36_ArticleListAllPriceCategoriesReportMutation,
  Create_R36_ArticleListAllPriceCategoriesReportMutationVariables,
  Create_R50_ArticleListWithImageReportMutation,
  Create_R50_ArticleListWithImageReportMutationVariables,
  Create_R6_ArticleListReportMutation,
  Create_R6_ArticleListReportMutationVariables,
  ExtractXmlFromPdfOfInvoicesMutation,
  ExtractXmlFromPdfOfInvoicesMutationVariables,
  GetTasksStatusQuery,
  ListWa_DataForMultiEmailsQuery,
  ListWa_DataForMultiEmailsQueryVariables,
  ListWa_PdfHistoryReportsQuery,
  ListWa_PdfHistoryReportsQueryVariables,
  SendEmailWithDocumentMutation,
  SendEmailWithDocumentMutationVariables,
  Create_R8_DeliveryNoteA4WithPriceReportMutation,
  Create_R8_DeliveryNoteA4WithPriceReportMutationVariables,
  Create_R29_DeliveryNoteA5LandscapeWithPriceReportMutation,
  Create_R29_DeliveryNoteA5LandscapeWithPriceReportMutationVariables,
  Create_R9_InvoiceA4ReportMutation,
  Create_R9_InvoiceA4ReportMutationVariables,
  Create_R59_InvoicePaymentSlipWithQrReportMutation,
  Create_R59_InvoicePaymentSlipWithQrReportMutationVariables,
  Create_R10_CustomersDebtNotifierMutation,
  Create_R10_CustomersDebtNotifierMutationVariables,
  Create_R24_PaymentJournalA4Mutation,
  Create_R24_PaymentJournalA4MutationVariables,
  Create_R35_ExtendedPaymentJournalMutation,
  Create_R35_ExtendedPaymentJournalMutationVariables,
  Create_R22_BesrImportA4Mutation,
  Create_R22_BesrImportA4MutationVariables,
  Create_R37_OfferA4Mutation,
  Create_R37_OfferA4MutationVariables,
  Create_R21_InvoiceJournalReportMutationVariables,
  Create_R21_InvoiceJournalReportMutation,
  Create_R23_DebitorJournalA4LandscapeMutation,
  Create_R23_DebitorJournalA4LandscapeMutationVariables,
} from '../../../graphql/generatedModel';
import { gqlClient } from '../../../graphql/graphqlRequest';
import { localeFormatterHelper } from '../../../shared/helpers/formatter/localeFormatter.helper';
import { Pub, Service, Sub } from '../service.abstract';
import { responseHandler } from '../../../shared/responseHandler/responseHandler';
import {
  r36_ArticleReportAllPriceCategoriesPreview,
  r6_ArticleReportPreview,
  r50_ArticleReportWithImagePreview,
  extractXmlFromPdfOfInvoices,
  getDataForMultiEmails,
  getTasksStatus,
  listWAPdfHistoryReports,
  sendEmailWithDocument,
  r3_CustomerReportAddressesListPreview,
  r4_CustomerReportFactSheetPreview,
  r5_CustomerReportPhonesListPreview,
  r8_DeliveryNoteA4WithPricePreview,
  r29_DeliveryNoteA5LandscapeWithPricePreview,
  r9_InvoiceA4Preview,
  r59_InvoicePaymentSlipWithQRPreview,
  r22_BESRImportJournalPreview,
  r10_CustomersDebtNotifierPreview,
  r24_PaymentJournalA4Preview,
  r35_ExtendedPaymentJournalPreview,
  r37_OfferA4Preview,
  r21_InvoiceJournalReport,
  r7_WeeklyOrderingList,
  r28_WeeklyOrderingList_blank_quantity,
  r33_PriceListForCustomer,
  r47_WeeklyOrderingList_blank_quantity_blank_date,
  r23_DebitorJournalA4LandscapePreview,
} from './gql/reports.gql';
import { getFirstDayOfWeek } from '../../components/datePicker/weekPicker.component';

type Actions =
  | 'getPdfHistoryReport'
  | 'listPdfHistoryReports'
  | 'sendEmailWithDocument'
  | 'sendMultiEmailsWithDocument'
  | 'extractXmlFromPdfOfInvoices'
  | 'getDataForMultiEmails'
  | 'createArticlesReport'
  | 'createCustomersReport'
  | 'createOfferReport'
  | 'createOrderReport'
  | 'createInvoiceReport'
  | 'createBESRImportJournalReport'
  | 'createDebtReminderReport'
  | 'createMultipleDebtReminderReports'
  | 'createDebtReminderWithInvoiceReport'
  | 'createMultipleDebtReminderWithInvoiceReports'
  | 'createPaymentJournalReport'
  | 'createDebitorJournalA4Landscape'
  | undefined;

export const REPORTS_TASK_STATUS_POLLING_TIMEOUT_ERROR =
  'reports.taskStatusPollingMaxAttemptsExceeded';
// A report task finished in a non-READY state (WRTS3_ERROR_BUILDING,
// WRTS9_ERROR_CREATING_PDF, ...). The failing status is appended to the marker so
// Sentry groups by failure kind: before WA-3801 these were bare `new Error()`, which
// left `IApiError.message` undefined, so `responseHandler` reported
// `captureMessage('unknown')` and left us with no way to tell a build failure from a
// polling timeout. The status is a bounded enum, so appending it does not fragment
// grouping the way a task id would.
export const REPORTS_TASK_FAILED_ERROR = 'reports.taskFailed';
// A polled task id has no matching generate-request result: the polling response and
// the generate response disagree, which is a client-side bug rather than a report
// problem, and is worth telling apart from REPORTS_TASK_FAILED_ERROR.
export const REPORTS_TASK_RESULT_MISSING_ERROR = 'reports.taskResultMissing';
// R64 needs the notifier PDF plus at least one invoice PDF; without both, the reminder
// cannot be assembled.
export const REPORTS_NOTIFIER_FILES_MISSING_ERROR = 'reports.notifierFilesMissing';
/**
 * Why R64 could not be assembled, appended to the marker the same way `REPORTS_TASK_FAILED_ERROR`
 * appends the task status (WA-3801): a bounded set of reasons keeps the Sentry grouping intact
 * while making the events tell each other apart, and lets the caller treat the one *legitimate*
 * reason differently from the two broken ones.
 *
 * - `noData`   — the reminder itself had nothing to print (`WRTS15_NO_DATA_FOR_REPORT`). Not a
 *                bug: the R10-only preview shows "no data" for exactly this and files nothing,
 *                and a batch drops such a customer (WA-3865). Was reaching the user as
 *                `errors.unknown` plus an auto-filed ticket per click (WA-3868).
 * - `notifier` — the notifier task ended in some other non-READY state, i.e. it really failed.
 * - `invoices` — the notifier exists but no archived invoice PDF came back for the request.
 */
export type TNotifierFilesMissingReason = 'noData' | 'notifier' | 'invoices';
export const notifierFilesMissingError = (reason: TNotifierFilesMissingReason): string =>
  `${REPORTS_NOTIFIER_FILES_MISSING_ERROR}: ${reason}`;

/**
 * What a `responseHandler` `customErrorHandler` should return for a thrown reports marker: the
 * caller-chosen `notice` with Sentry suppressed for the one legitimate reason, and `undefined`
 * for everything else, which leaves the default (`errors.unknown` + a Sentry report, which
 * auto-files a Jira ticket) in place. Shared by the R64 preview and e-mail paths so a
 * "nothing to remind" outcome cannot regress into a filed ticket in one of them (WA-3868); the
 * notice differs per caller because "no preview" makes no sense for an e-mail.
 */
export const noDataNoticeForReportsError = (
  errorMessage: string | undefined,
  notice: string,
): { message: string; reportToSentry: boolean } | undefined =>
  errorMessage === notifierFilesMissingError('noData')
    ? { message: notice, reportToSentry: false }
    : undefined;

/**
 * What a finished task means for a BATCH of per-customer reports (WA-3865, Sentry
 * WAWIASSIST-24R). `WRTS15_NO_DATA_FOR_REPORT` is the report engine saying it had nothing
 * to print for that one customer (e.g. every invoice in the list is below the reminder
 * level by the payments-till date) — a legitimate outcome, and the one every single-report
 * path already treats as "no data" rather than as a failure. In a batch it must not sink
 * the other customers' reminders, so such a task is dropped and the caller learns which
 * customers are missing by comparing the result with what it asked for
 * (`getCustomersWithoutNotifierFile`). Every other non-READY status is a real failure
 * and keeps throwing `REPORTS_TASK_FAILED_ERROR`.
 */
const hasReportData = (item: GetWinReportsStatusRes[number]): boolean => {
  if (item.status === C_Win_Report_Task_Status.WRTS15_NO_DATA_FOR_REPORT) return false;
  if (item.status !== C_Win_Report_Task_Status.WRTS2_READY) {
    throw new Error(`${REPORTS_TASK_FAILED_ERROR}: ${item.status}`);
  }
  return true;
};

class PubImpl extends Pub<Actions> {
  listPdfHistoryReports(params: ListWa_PdfHistoryReportsQueryVariables) {
    this.emit('listPdfHistoryReports', params);
  }

  sendEmailWithDocument(params: SendEmailWithDocumentMutationVariables): void {
    this.emit('sendEmailWithDocument', params);
  }

  sendMultiEmailsWithDocument(params: SendEmailWithDocumentMutationVariables[]): void {
    this.emit('sendMultiEmailsWithDocument', params);
  }

  extractXmlFromPdfOfInvoices(params: ExtractXmlFromPdfOfInvoicesMutationVariables) {
    this.emit('extractXmlFromPdfOfInvoices', params);
  }

  getDataForMultiEmails(params: ListWa_DataForMultiEmailsQueryVariables) {
    this.emit('getDataForMultiEmails', params);
  }
  createArticlesReport(params: IArticlesReportParams): void {
    this.emit('createArticlesReport', params);
  }
  createCustomersReport(params: ICustomersReportParams): void {
    this.emit('createCustomersReport', params);
  }
  createOfferReport(params: IOfferReportParams): void {
    this.emit('createOfferReport', params);
  }
  createOrderReport(params: IOrderReportParams): void {
    this.emit('createOrderReport', params);
  }
  createInvoiceReport(params: IInvoiceReportParams): void {
    this.emit('createInvoiceReport', params);
  }
  createBESRImportJournalReport(params: Create_R22_BesrImportA4MutationVariables) {
    this.emit('createBESRImportJournalReport', params);
  }
  createDebtReminderReport(params: Create_R10_CustomersDebtNotifierMutationVariables) {
    this.emit('createDebtReminderReport', params);
  }
  createDebitorJournalA4Landscape(params: Create_R23_DebitorJournalA4LandscapeMutationVariables) {
    this.emit('createDebitorJournalA4Landscape', params);
  }
  createMultipleDebtReminderReports(
    params: Array<Create_R10_CustomersDebtNotifierMutationVariables>,
  ) {
    this.emit('createMultipleDebtReminderReports', params);
  }
  createDebtReminderWithInvoiceReport(params: IGetR64NotifierFilesParams) {
    this.emit('createDebtReminderWithInvoiceReport', params);
  }
  createMultipleDebtReminderWithInvoiceReports(params: IGetMultipleR64NotifierFilesParams) {
    this.emit('createMultipleDebtReminderWithInvoiceReports', params);
  }
  createPaymentJournalReport(params: IPaymentJournalReportParams) {
    this.emit('createPaymentJournalReport', params);
  }
  clearStream() {
    this.emit(undefined, {});
  }
}

class SubImpl extends Sub<Actions> {
  listPdfHistoryReports(): Observable<ListWAPdfHistoryReportRes> {
    return this.actionListener('listPdfHistoryReports').pipe(
      switchMap(({ params }) => {
        return gqlClient(
          listWAPdfHistoryReports,
          params,
        ) as Observable<ListWa_PdfHistoryReportsQuery>;
      }),
      map((data) => data?.wawiAssist?.listWA_PdfHistoryReports as ListWAPdfHistoryReportRes),
    );
  }
  waitAndListPdfHistoryReports(): Observable<ListWAPdfHistoryReportRes> {
    return this.listPdfHistoryReports().pipe(
      this._retryer({
        retryCondition: ({ response }) =>
          // `response` is absent on the `Session expired` error gqlClient throws
          // (it is a plain Error, not a ClientError); optional-chain so this
          // returns false (don't retry) instead of throwing a TypeError.
          response?.errors?.[0]?.extensions?.code === C_Report_Error_Code.PDF_IS_NOT_GENERATED,
      }),
    );
  }

  private _retryer<T>(options: IRetryerArgs) {
    const { retryCondition, attempts = 10, interval = 1000 } = options || {};
    return (stream: Observable<T>) => {
      return stream.pipe(
        retry({
          count: attempts,
          delay: (v: ClientError) => {
            const cond = retryCondition(v);
            if (cond) {
              return from([v]).pipe(delay(interval));
            }
            throw v;
          },
        }),
      );
    };
  }

  sendEmailWithDocument(): Observable<SendEmailWithDocumentPlusParamsRes> {
    return this.actionListener('sendEmailWithDocument').pipe(
      switchMap(({ params }: { params: SendEmailWithDocumentMutationVariables }) => {
        const { docType, documentId } = params;
        return zip(
          gqlClient(sendEmailWithDocument, params) as Observable<SendEmailWithDocumentMutation>,
          of({ docType, documentId }),
        );
      }),
      map(([res, variables]) => {
        return {
          ...res.wawiAssist?.sendEmailWithDocument,
          ...variables,
        } as SendEmailWithDocumentPlusParamsRes;
      }),
    );
  }

  sendMultiEmailsWithDocument(): Observable<SendEmailWithDocumentPlusParamsRes[]> {
    return this.actionListener('sendMultiEmailsWithDocument').pipe(
      switchMap(({ params }: { params: SendEmailWithDocumentMutationVariables[] }) => {
        const emailsRequests = params.map((variables) =>
          gqlClient(sendEmailWithDocument, { ...variables }).pipe(
            map((res: SendEmailWithDocumentMutation) => ({ res, variables })),
          ),
        );
        return forkJoin(emailsRequests).pipe(
          map((responses) => {
            return responses.map(({ res, variables }) => {
              const response = res.wawiAssist?.sendEmailWithDocument as SendEmailWithDocumentRes;
              const { docType, documentId } = variables;
              return {
                ...response,
                docType,
                documentId,
              } as SendEmailWithDocumentPlusParamsRes;
            });
          }),
        );
      }),
    );
  }

  extractXmlFromPdfOfInvoices(): Observable<ExtractXmlFromPdfOfInvoicesRes> {
    return this.actionListener('extractXmlFromPdfOfInvoices').pipe(
      switchMap(({ params }) => {
        return gqlClient(
          extractXmlFromPdfOfInvoices,
          params,
        ) as Observable<ExtractXmlFromPdfOfInvoicesMutation>;
      }),
      map(
        (data) => data?.wawiAssist?.extractXmlFromPdfOfInvoices as ExtractXmlFromPdfOfInvoicesRes,
      ),
    );
  }

  getDataForMultiEmails(): Observable<DataForMultiEmailsRes> {
    return this.actionListener('getDataForMultiEmails').pipe(
      switchMap(({ params }) => {
        return gqlClient(
          getDataForMultiEmails,
          params,
        ) as Observable<ListWa_DataForMultiEmailsQuery>;
      }),
      map((data) => data.wawiAssist?.listWA_DataForMultiEmails as DataForMultiEmailsRes),
    );
  }
  createArticlesReport(): Observable<WindowsGeneratedReportPreviewRes> {
    return this.actionListener('createArticlesReport').pipe(
      switchMap(({ params }: { params: IArticlesReportParams }) => {
        switch (params.reportId) {
          case C_Report.R6_ARTICLE_LIST: {
            const { articlesIds, priceValidOn, priceMode, customerId, priceCategory } = params;
            const variables: Create_R6_ArticleListReportMutationVariables = {
              params: {
                articlesIds,
                priceValidOn: format(
                  localeFormatterHelper.localizedDate(new Date(priceValidOn)),
                  'yyyy-MM-dd',
                ),
              },
            };

            if (priceMode === 'byCustomer') {
              variables.params.customerId = customerId;
            }
            if (priceMode === 'byPriceCategory') {
              variables.params.priceCategory = priceCategory;
            }
            return gqlClient(r6_ArticleReportPreview, variables).pipe(
              map((res: Create_R6_ArticleListReportMutation) => {
                return res.wawiAssist
                  ?.create_r6_ArticleListReport as WindowsGeneratedReportPreviewRes;
              }),
            );
          }

          case C_Report.R50_ARTICLE_LIST_WITH_IMAGES: {
            const { articlesIds, priceValidOn, priceMode, customerId, priceCategory } = params;
            const variables: Create_R50_ArticleListWithImageReportMutationVariables = {
              params: {
                articlesIds,
                priceValidOn: format(
                  localeFormatterHelper.localizedDate(new Date(priceValidOn)),
                  'yyyy-MM-dd',
                ),
              },
            };

            if (priceMode === 'byCustomer') {
              variables.params.customerId = customerId;
            }
            if (priceMode === 'byPriceCategory') {
              variables.params.priceCategory = priceCategory;
            }
            return gqlClient(r50_ArticleReportWithImagePreview, variables).pipe(
              map((res: Create_R50_ArticleListWithImageReportMutation) => {
                return res.wawiAssist
                  ?.create_r50_ArticleListWithImageReport as WindowsGeneratedReportPreviewRes;
              }),
            );
          }

          case C_Report.R36_ARTICLE_LIST_ALL_PRICE_CATEGORIES: {
            const { articlesIds, priceValidOn } = params;
            const variables: Create_R36_ArticleListAllPriceCategoriesReportMutationVariables = {
              params: {
                articlesIds,
                priceValidOn: format(
                  localeFormatterHelper.localizedDate(new Date(priceValidOn)),
                  'yyyy-MM-dd',
                ),
              },
            };
            return gqlClient(r36_ArticleReportAllPriceCategoriesPreview, variables).pipe(
              map((res: Create_R36_ArticleListAllPriceCategoriesReportMutation) => {
                return res.wawiAssist
                  ?.create_r36_ArticleListAllPriceCategoriesReport as WindowsGeneratedReportPreviewRes;
              }),
            );
          }
          default:
            return EMPTY;
        }
      }),
      delay(2000),
      switchMap((reportTaskResult) => {
        const { taskId } = reportTaskResult;
        return this._taskStatusPolling({ tasksId: [taskId] }).pipe(
          map((tasks) => {
            const [item] = tasks;
            reportTaskResult.status = item.status;
            return reportTaskResult;
          }),
        );
      }),
      finalize(() => reportsService.pub.clearStream()),
    );
  }
  createCustomersReport(): Observable<WindowsGeneratedReportPreviewRes> {
    return this.actionListener('createCustomersReport').pipe(
      switchMap(({ params }: { params: ICustomersReportParams }) => {
        switch (params.reportId) {
          case C_Report.R3_ADDRESS_LIST_A4_LANDSCAPE: {
            const { customersIds } = params;
            const variables: Create_R3_CustomerAddressesListReportMutationVariables = {
              customersIds,
            };
            return gqlClient(r3_CustomerReportAddressesListPreview, variables).pipe(
              map((res: Create_R3_CustomerAddressesListReportMutation) => {
                return res.wawiAssist
                  ?.create_r3_CustomerAddressesListReport as WindowsGeneratedReportPreviewRes;
              }),
            );
          }

          case C_Report.R4_FACTSHEET: {
            const { customersIds } = params;
            const variables: Create_R4_CustomerFactSheetReportMutationVariables = {
              customersIds,
            };
            return gqlClient(r4_CustomerReportFactSheetPreview, variables).pipe(
              map((res: Create_R4_CustomerFactSheetReportMutation) => {
                return res.wawiAssist
                  ?.create_r4_CustomerFactSheetReport as WindowsGeneratedReportPreviewRes;
              }),
            );
          }

          case C_Report.R5_PHONES_LIST: {
            const { customersIds } = params;
            const variables: Create_R5_CustomerPhonesListReportMutationVariables = {
              customersIds,
            };
            return gqlClient(r5_CustomerReportPhonesListPreview, variables).pipe(
              map((res: Create_R5_CustomerPhonesListReportMutation) => {
                return res.wawiAssist
                  ?.create_r5_CustomerPhonesListReport as WindowsGeneratedReportPreviewRes;
              }),
            );
          }

          case C_Report.R7_WEEKLY_ORDERING_LIST: {
            const { customersIds } = params;
            let { customerId, periodStart, doIncludeAllArticles } = params;
            customerId = customerId ?? (customersIds.length > 0 ? customersIds[0] : '0');
            periodStart = periodStart ?? getFirstDayOfWeek();
            doIncludeAllArticles = doIncludeAllArticles ?? false;
            const variables: Create_R7_WeeklyOrderingListMutationVariables = {
              params: {
                customerId,
                periodStart: format(
                  localeFormatterHelper.localizedDate(new Date(periodStart)),
                  'yyyy-MM-dd',
                ),
                doIncludeAllArticles,
              },
            };
            return gqlClient(r7_WeeklyOrderingList, variables).pipe(
              map((res: Create_R7_WeeklyOrderingListMutation) => {
                return res.wawiAssist
                  ?.create_r7_WeeklyOrderingList as WindowsGeneratedReportPreviewRes;
              }),
            );
          }

          case C_Report.R28_WEEKLY_ORDERING_LIST_BLANK_QUANTITY: {
            const { customersIds } = params;
            let { customerId, periodStart, doIncludeAllArticles } = params;
            customerId = customerId ?? (customersIds.length > 0 ? customersIds[0] : '0');
            periodStart = periodStart ?? getFirstDayOfWeek();
            doIncludeAllArticles = doIncludeAllArticles ?? false;
            const variables: Create_R28_WeeklyOrderingList_Blank_QuantityMutationVariables = {
              params: {
                customerId,
                periodStart: format(
                  localeFormatterHelper.localizedDate(new Date(periodStart)),
                  'yyyy-MM-dd',
                ),
                doIncludeAllArticles,
              },
            };
            return gqlClient(r28_WeeklyOrderingList_blank_quantity, variables).pipe(
              map((res: Create_R28_WeeklyOrderingList_Blank_QuantityMutation) => {
                return res.wawiAssist
                  ?.create_r28_WeeklyOrderingList_blank_quantity as WindowsGeneratedReportPreviewRes;
              }),
            );
          }

          case C_Report.R33_PRICE_LIST_FOR_CUSTOMER: {
            const { customersIds } = params;
            let { customerId, periodStart, doIncludeAllArticles } = params;
            customerId = customerId ?? (customersIds.length > 0 ? customersIds[0] : '0');
            periodStart = periodStart ?? getFirstDayOfWeek();
            doIncludeAllArticles = doIncludeAllArticles ?? false;
            const variables: Create_R33_PriceListForCustomerMutationVariables = {
              params: {
                customerId,
                periodStart: format(
                  localeFormatterHelper.localizedDate(new Date(periodStart)),
                  'yyyy-MM-dd',
                ),
                doIncludeAllArticles,
              },
            };
            return gqlClient(r33_PriceListForCustomer, variables).pipe(
              map((res: Create_R33_PriceListForCustomerMutation) => {
                return res.wawiAssist
                  ?.create_r33_PriceListForCustomer as WindowsGeneratedReportPreviewRes;
              }),
            );
          }

          case C_Report.R47_WEEKLY_ORDERINGLIST_BLANK_QUANTITY_BLANK_DATE: {
            const { customersIds } = params;
            let { customerId, periodStart, doIncludeAllArticles } = params;
            customerId = customerId ?? (customersIds.length > 0 ? customersIds[0] : '0');
            periodStart = periodStart ?? getFirstDayOfWeek();
            doIncludeAllArticles = doIncludeAllArticles ?? false;
            const variables: Create_R47_WeeklyOrderingList_Blank_Quantity_Blank_DateMutationVariables =
              {
                params: {
                  customerId,
                  periodStart: format(
                    localeFormatterHelper.localizedDate(new Date(periodStart)),
                    'yyyy-MM-dd',
                  ),
                  doIncludeAllArticles,
                },
              };
            return gqlClient(r47_WeeklyOrderingList_blank_quantity_blank_date, variables).pipe(
              map((res: Create_R47_WeeklyOrderingList_Blank_Quantity_Blank_DateMutation) => {
                return res.wawiAssist
                  ?.create_r47_WeeklyOrderingList_blank_quantity_blank_date as WindowsGeneratedReportPreviewRes;
              }),
            );
          }

          default:
            return EMPTY;
        }
      }),
      delay(2000),
      switchMap((reportTaskResult) => {
        const { taskId } = reportTaskResult;
        return this._taskStatusPolling({ tasksId: [taskId] }).pipe(
          map((tasks) => {
            const [item] = tasks;
            reportTaskResult.status = item.status;
            return reportTaskResult;
          }),
        );
      }),
      finalize(() => reportsService.pub.clearStream()),
    );
  }
  createOfferReport(): Observable<WindowsGeneratedReportPreviewRes> {
    return this.actionListener('createOfferReport').pipe(
      switchMap(({ params }: { params: IOfferReportParams }) => {
        switch (params.reportId) {
          case C_Report.R37_OFFER_A4: {
            const { offerId } = params;
            const variables: Create_R37_OfferA4MutationVariables = {
              offerId,
            };
            return gqlClient(r37_OfferA4Preview, variables).pipe(
              map((res: Create_R37_OfferA4Mutation) => {
                return res.wawiAssist?.create_r37_OfferA4 as WindowsGeneratedReportPreviewRes;
              }),
            );
          }

          default:
            return EMPTY;
        }
      }),
      delay(2000),
      switchMap((reportTaskResult) => {
        const { taskId } = reportTaskResult;
        return this._taskStatusPolling({ tasksId: [taskId] }).pipe(
          map((tasks) => {
            const [item] = tasks;
            reportTaskResult.status = item.status;
            return reportTaskResult;
          }),
        );
      }),
      finalize(() => reportsService.pub.clearStream()),
    );
  }
  createOrderReport(): Observable<WindowsGeneratedReportPreviewRes> {
    return this.actionListener('createOrderReport').pipe(
      switchMap(({ params }: { params: IOrderReportParams }) => {
        switch (params.reportId) {
          case C_Report.R8_DELIVERY_NOTE_A4_WITH_PRICE: {
            const { orderId } = params;
            const variables: Create_R8_DeliveryNoteA4WithPriceReportMutationVariables = {
              orderId,
            };
            return gqlClient(r8_DeliveryNoteA4WithPricePreview, variables).pipe(
              map((res: Create_R8_DeliveryNoteA4WithPriceReportMutation) => {
                return res.wawiAssist
                  ?.create_r8_DeliveryNoteA4WithPriceReport as WindowsGeneratedReportPreviewRes;
              }),
            );
          }

          case C_Report.R29_DELIVERY_NOTE_A5_LANDSCAPE_WITH_PRICE: {
            const { orderId } = params;
            const variables: Create_R29_DeliveryNoteA5LandscapeWithPriceReportMutationVariables = {
              orderId,
            };
            return gqlClient(r29_DeliveryNoteA5LandscapeWithPricePreview, variables).pipe(
              map((res: Create_R29_DeliveryNoteA5LandscapeWithPriceReportMutation) => {
                return res.wawiAssist
                  ?.create_r29_DeliveryNoteA5LandscapeWithPriceReport as WindowsGeneratedReportPreviewRes;
              }),
            );
          }

          default:
            return EMPTY;
        }
      }),
      delay(2000),
      switchMap((reportTaskResult) => {
        const { taskId } = reportTaskResult;
        return this._taskStatusPolling({ tasksId: [taskId] }).pipe(
          map((tasks) => {
            const [item] = tasks;
            reportTaskResult.status = item.status;
            return reportTaskResult;
          }),
        );
      }),
      finalize(() => reportsService.pub.clearStream()),
    );
  }
  createInvoiceReport(): Observable<WindowsGeneratedReportPreviewRes> {
    return this.actionListener('createInvoiceReport').pipe(
      switchMap(({ params }: { params: IInvoiceReportParams }) => {
        switch (params.reportId) {
          case C_Report.R9_INVOICE_A4: {
            const { invoiceId } = params;
            if (invoiceId) {
              const variables: Create_R9_InvoiceA4ReportMutationVariables = {
                invoiceId,
              };
              return gqlClient(r9_InvoiceA4Preview, variables).pipe(
                map((res: Create_R9_InvoiceA4ReportMutation) => {
                  return res.wawiAssist
                    ?.create_r9_InvoiceA4Report as WindowsGeneratedReportPreviewRes;
                }),
              );
            }
            return EMPTY;
          }

          case C_Report.R59_INVOICE_PAYMENTSLIP_WITH_QR: {
            const { invoiceId } = params;
            if (invoiceId) {
              const variables: Create_R59_InvoicePaymentSlipWithQrReportMutationVariables = {
                invoiceId,
              };
              return gqlClient(r59_InvoicePaymentSlipWithQRPreview, variables).pipe(
                map((res: Create_R59_InvoicePaymentSlipWithQrReportMutation) => {
                  return res.wawiAssist
                    ?.create_r59_InvoicePaymentSlipWithQRReport as WindowsGeneratedReportPreviewRes;
                }),
              );
            }
            return EMPTY;
          }

          case C_Report.R21_INVOICE_JOURNAL: {
            const { invoicesIds } = params;
            if (invoicesIds) {
              const variables: Create_R21_InvoiceJournalReportMutationVariables = {
                invoicesIds,
              };
              return gqlClient(r21_InvoiceJournalReport, variables).pipe(
                map((res: Create_R21_InvoiceJournalReportMutation) => {
                  return res.wawiAssist
                    ?.create_r21_InvoiceJournalReport as WindowsGeneratedReportPreviewRes;
                }),
              );
            }
            return EMPTY;
          }

          default:
            return EMPTY;
        }
      }),
      delay(2000),
      switchMap((reportTaskResult) => {
        const { taskId } = reportTaskResult;
        return this._taskStatusPolling({ tasksId: [taskId] }).pipe(
          map((tasks) => {
            const [item] = tasks;
            reportTaskResult.status = item.status;
            return reportTaskResult;
          }),
        );
      }),
      finalize(() => reportsService.pub.clearStream()),
    );
  }
  createBESRImportJournalReport(): Observable<WindowsGeneratedReportPreviewRes> {
    return this.actionListener('createBESRImportJournalReport').pipe(
      switchMap(({ params }) => {
        return gqlClient(r22_BESRImportJournalPreview, params).pipe(
          map((res: Create_R22_BesrImportA4Mutation) => {
            return res.wawiAssist?.create_r22_BESRImportA4 as WindowsGeneratedReportPreviewRes;
          }),
        );
      }),
      delay(2000),
      switchMap((reportTaskResult) => {
        const { taskId } = reportTaskResult;
        return this._taskStatusPolling({ tasksId: [taskId] }).pipe(
          map((tasks) => {
            const [item] = tasks;
            reportTaskResult.status = item.status;
            return reportTaskResult;
          }),
        );
      }),
      finalize(() => reportsService.pub.clearStream()),
    );
  }
  createDebtReminderReport(): Observable<IGetR10NotifierFileRes> {
    return this.actionListener('createDebtReminderReport').pipe(
      this._queryR10Notifier(),
      finalize(() => reportsService.pub.clearStream()),
    );
  }
  createDebitorJournalA4Landscape(): Observable<IGetR23DebitorJournalA4LandscapeFileRes> {
    return this.actionListener('createDebitorJournalA4Landscape').pipe(
      this._queryR23Journal(),
      finalize(() => reportsService.pub.clearStream()),
    );
  }
  createMultipleDebtReminderReports(): Observable<IGetMultipleR10NotifierFilesRes> {
    return this.actionListener('createMultipleDebtReminderReports').pipe(
      switchMap(({ params }) => {
        const requests: Array<Observable<GetR10NotifierRequestRes & { customerId: string }>> =
          params.map((gqlVariables: Create_R10_CustomersDebtNotifierMutationVariables) =>
            gqlClient(r10_CustomersDebtNotifierPreview, gqlVariables).pipe(
              map(
                (res: Create_R10_CustomersDebtNotifierMutation) =>
                  res.wawiAssist?.create_r10_CustomersDebtNotifier,
              ),
              map((res) => ({ ...res, customerId: gqlVariables.params.customerId })),
            ),
          );
        return forkJoin(requests);
      }),
      delay(5000),
      switchMap((res) => {
        const tasksId = res.map((item) => item.taskId);

        return this._taskStatusPolling({ tasksId, maxPollingAttempts: 20 }).pipe(
          map((v) => {
            const items = v.filter(hasReportData).map((item) => {
              const generateResItem = res.find((it) => it.taskId === item.taskId);
              if (!generateResItem) {
                throw new Error(REPORTS_TASK_RESULT_MISSING_ERROR);
              }
              return generateResItem;
            });

            return items;
          }),
        );
      }),
      finalize(() => reportsService.pub.clearStream()),
    );
  }
  createDebtReminderWithInvoiceReport(): Observable<IGetR64NotifierFilesRes> {
    return this.actionListener('createDebtReminderWithInvoiceReport').pipe(
      map((v) => {
        const { r10NotifierParams, listPdfHistoryReportParams } =
          v.params as IGetR64NotifierFilesParams;
        reportsService.pub.listPdfHistoryReports(listPdfHistoryReportParams);
        v.params = r10NotifierParams;
        return v;
      }),
      this._queryR10Notifier(),
      zipWith(
        reportsService.sub
          .waitAndListPdfHistoryReports()
          .pipe(responseHandler<ListWAPdfHistoryReportRes>({ errorReturnType: { reports: [] } })),
      ),
      map((v) => {
        // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain -- pre-existing assertion; kept as-is to avoid changing runtime behavior
        const notifierPdfUrl = v?.[0]?.url!;
        const notifierPdfS3Key = v?.[0]?.s3Key;
        const invoicesPdfs = v?.[1]?.reports || [];
        if (!notifierPdfS3Key || !invoicesPdfs.length) {
          let reason: TNotifierFilesMissingReason = 'invoices';
          if (!notifierPdfS3Key) reason = v?.[0]?.noData ? 'noData' : 'notifier';
          throw new Error(notifierFilesMissingError(reason));
        }
        const resInvoices = invoicesPdfs.filter(
          (invoiceReport) =>
            invoiceReport.reportId !== C_Report.R17_INVOICE_SUMMARY &&
            invoiceReport.reportId !== C_Report.R52_DELIVERY_OVERVIEW,
        );

        return {
          notifierPdfUrl,
          notifierPdfS3Key,
          invoicesPdfs: resInvoices,
        };
      }),
      finalize(() => reportsService.pub.clearStream()),
    );
  }
  createMultipleDebtReminderWithInvoiceReports(): Observable<IGetMultipleR64NotifierFilesRes> {
    return this.actionListener('createMultipleDebtReminderWithInvoiceReports').pipe(
      tap((v) => {
        const params: IGetMultipleR64NotifierFilesParams = v.params;
        const invoicesId = Object.values(params).reduce<Array<string>>((outArr, item) => {
          return [...outArr, ...item.invoiceIds];
        }, []);
        reportsService.pub.listPdfHistoryReports({
          params: {
            docsType: C_History_Pdf_Doctype.PDF_INVOICE,
            documentsIds: invoicesId,
          },
        });
      }),
      switchMap((v) => {
        const params = v.params as IGetMultipleR64NotifierFilesParams;
        const r10Requests = Object.values(params).map(({ r10NotifierParams }) =>
          gqlClient(r10_CustomersDebtNotifierPreview, r10NotifierParams).pipe(
            map(
              (res: Create_R10_CustomersDebtNotifierMutation) =>
                res.wawiAssist?.create_r10_CustomersDebtNotifier as GetR10NotifierRequestRes,
            ),
            map((res) => ({ ...res, customerId: r10NotifierParams.params.customerId })),
          ),
        );
        return zip(
          forkJoin(r10Requests),
          reportsService.sub
            .waitAndListPdfHistoryReports()
            .pipe(responseHandler<ListWAPdfHistoryReportRes>({ errorReturnType: { reports: [] } })),
          of(params),
        );
      }),
      delay(5000),
      switchMap((res) => {
        const [notifiersRes, invoicesRes, params] = res || [];
        const tasksId = notifiersRes?.map((item) => item.taskId);

        return this._taskStatusPolling({ tasksId, maxPollingAttempts: 20 }).pipe(
          map((v) => {
            const items = v.filter(hasReportData).map((item) => {
              const generateResItem = res?.[0]?.find((it) => it.taskId === item.taskId);
              if (!generateResItem) {
                throw new Error(REPORTS_TASK_RESULT_MISSING_ERROR);
              }
              const { url, s3Key, customerId } = generateResItem || {};
              const customerInvoicesId = params[customerId]?.invoiceIds || [];
              const invoicesPdfs =
                invoicesRes.reports.reduce(
                  (outArr: IGetR64NotifierFilesRes['invoicesPdfs'], invoiceReport) => {
                    const isInvoiceOfCustomer = !!customerInvoicesId.find(
                      (invoiceId) => invoiceId === invoiceReport.documentId,
                    );
                    const isInvoiceSummaryReport =
                      invoiceReport.reportId === C_Report.R17_INVOICE_SUMMARY ||
                      invoiceReport.reportId === C_Report.R52_DELIVERY_OVERVIEW;
                    if (isInvoiceOfCustomer && !isInvoiceSummaryReport) {
                      outArr.push(invoiceReport);
                    }
                    return outArr;
                  },
                  [],
                ) || [];

              return {
                notifierPdfUrl: url,
                notifierPdfS3Key: s3Key,
                invoicesPdfs,
                customerId,
              };
            });

            return items;
          }),
        );
      }),
      finalize(() => reportsService.pub.clearStream()),
    );
  }
  createPaymentJournalReport(): Observable<WindowsGeneratedReportPreviewRes> {
    return this.actionListener('createPaymentJournalReport').pipe(
      switchMap(({ params }: { params: IPaymentJournalReportParams }) => {
        switch (params.reportId) {
          case C_Report.R24_PAYMENT_JOURNAL_A4_LANDSCAPE: {
            const { paymentsId } = params;
            const variables: Create_R24_PaymentJournalA4MutationVariables = {
              paymentsId,
            };
            return gqlClient(r24_PaymentJournalA4Preview, variables).pipe(
              map((res: Create_R24_PaymentJournalA4Mutation) => {
                return res.wawiAssist
                  ?.create_r24_PaymentJournalA4 as WindowsGeneratedReportPreviewRes;
              }),
            );
          }

          case C_Report.R35_EXTENDED_PAYMENT_JOURNAL: {
            const { paymentsId } = params;
            const variables: Create_R35_ExtendedPaymentJournalMutationVariables = {
              paymentsId,
            };
            return gqlClient(r35_ExtendedPaymentJournalPreview, variables).pipe(
              map((res: Create_R35_ExtendedPaymentJournalMutation) => {
                return res.wawiAssist
                  ?.create_r35_ExtendedPaymentJournal as WindowsGeneratedReportPreviewRes;
              }),
            );
          }

          default:
            return EMPTY;
        }
      }),
      delay(2000),
      switchMap((reportTaskResult) => {
        const { taskId } = reportTaskResult;
        return this._taskStatusPolling({ tasksId: [taskId] }).pipe(
          map((tasks) => {
            const [item] = tasks;
            reportTaskResult.status = item.status;
            return reportTaskResult;
          }),
        );
      }),
      finalize(() => reportsService.pub.clearStream()),
    );
  }

  private _queryR23Journal() {
    return (stream: Observable<any>): Observable<IGetR23DebitorJournalA4LandscapeFileRes> =>
      stream.pipe(
        switchMap(
          ({ params }) =>
            gqlClient(
              r23_DebitorJournalA4LandscapePreview,
              params,
            ) as Observable<Create_R23_DebitorJournalA4LandscapeMutation>,
        ),
        delay(2000),
        switchMap((res) => {
          const resData = res.wawiAssist
            ?.create_r23_DebitorJournalA4Landscape as IGetR23DebitorJournalA4LandscapeFileRes;
          const { taskId } = resData || {};
          const nonSuccessRes = {
            taskId,
            url: null,
            s3Key: null,
          };
          if (!taskId) {
            return of(nonSuccessRes);
          }
          return this._taskStatusPolling({ tasksId: [taskId] }).pipe(
            map((v) => {
              if (v?.[0].status !== C_Win_Report_Task_Status.WRTS2_READY) {
                return nonSuccessRes;
              }
              return resData;
            }),
          );
        }),
      );
  }

  private _queryR10Notifier() {
    return (stream: Observable<any>): Observable<IGetR10NotifierFileRes> =>
      stream.pipe(
        switchMap(
          ({ params }) =>
            gqlClient(
              r10_CustomersDebtNotifierPreview,
              params,
            ) as Observable<Create_R10_CustomersDebtNotifierMutation>,
        ),
        delay(2000),
        switchMap((res) => {
          const resData = res.wawiAssist
            ?.create_r10_CustomersDebtNotifier as IGetR10NotifierFileRes;
          const { taskId } = resData || {};
          const nonSuccessRes = {
            taskId,
            url: null,
            s3Key: null,
          };
          if (!taskId) {
            return of(nonSuccessRes);
          }
          return this._taskStatusPolling({ tasksId: [taskId] }).pipe(
            map((v) => {
              const status = v?.[0].status;
              // WRTS15 is the report engine saying it had nothing to print for this reminder —
              // the same legitimate outcome `hasReportData` drops from a batch (WA-3865), not a
              // failure. Every non-READY status otherwise collapses into `nonSuccessRes` (the
              // R10-only preview shows `production.no_data_no_preview` for all of them), so flag
              // this one: it is the difference between a report with no data and a report that
              // could not be built, and the R64 caller has to tell them apart (WA-3868).
              if (status === C_Win_Report_Task_Status.WRTS15_NO_DATA_FOR_REPORT) {
                return { ...nonSuccessRes, noData: true };
              }
              if (status !== C_Win_Report_Task_Status.WRTS2_READY) {
                return nonSuccessRes;
              }
              return resData;
            }),
          );
        }),
      );
  }

  private _taskStatusPolling({
    tasksId,
    maxPollingAttempts = 15,
  }: {
    tasksId: Array<number>;
    maxPollingAttempts?: number;
  }): Observable<GetWinReportsStatusRes> {
    return (gqlClient(getTasksStatus, { tasksId }) as Observable<GetTasksStatusQuery>).pipe(
      map((res: GetTasksStatusQuery) => {
        return res.wawiAssist?.getWinReportTasksStatuses as GetWinReportsStatusRes;
      }),
      repeat({ count: maxPollingAttempts, delay: 2000 }),
      takeWhile((tasks) => {
        return (
          tasks?.every(
            (item) =>
              item.status === C_Win_Report_Task_Status.WRTS5_IN_PROGRESS ||
              item.status === C_Win_Report_Task_Status.WRTS1_NOT_STARTED,
          ) as boolean,
          true
        );
      }),
      filter((tasks) => {
        const isInProcess = tasks?.some(
          (item) =>
            item.status === C_Win_Report_Task_Status.WRTS5_IN_PROGRESS ||
            item.status === C_Win_Report_Task_Status.WRTS1_NOT_STARTED,
        );
        return !isInProcess;
      }),
      take(1),
      throwIfEmpty(() => new Error(REPORTS_TASK_STATUS_POLLING_TIMEOUT_ERROR)),
    );
  }
}

class ReportsService extends Service<Actions> {
  pub = new PubImpl(this.stream$);
  sub = new SubImpl(this.stream$);
}

export const reportsService = new ReportsService();

interface IRetryerArgs {
  retryCondition: (err: ClientError) => boolean;
  attempts?: number;
  interval?: number;
}

export type ListWAPdfHistoryReportRes = NonNullable<
  NonNullable<ListWa_PdfHistoryReportsQuery['wawiAssist']>['listWA_PdfHistoryReports']
>;
export type SendEmailWithDocumentRes = NonNullable<
  NonNullable<SendEmailWithDocumentMutation['wawiAssist']>['sendEmailWithDocument']
>;
export interface SendEmailWithDocumentPlusParamsRes extends SendEmailWithDocumentRes {
  docType: C_Email_Msg_Doctype;
  documentId: number | string;
}
export type ExtractXmlFromPdfOfInvoicesRes = NonNullable<
  NonNullable<ExtractXmlFromPdfOfInvoicesMutation['wawiAssist']>['extractXmlFromPdfOfInvoices']
>;

export type DataForMultiEmailsRes = NonNullable<
  NonNullable<ListWa_DataForMultiEmailsQuery['wawiAssist']>['listWA_DataForMultiEmails']
>;

export type ReportIdsForArticle =
  | C_Report.R6_ARTICLE_LIST
  | C_Report.R36_ARTICLE_LIST_ALL_PRICE_CATEGORIES
  | C_Report.R50_ARTICLE_LIST_WITH_IMAGES;
export interface IArticlesReportParams {
  reportId: ReportIdsForArticle;
  articlesIds: Array<string>;
  priceValidOn: Date | string;
  priceMode: 'byCustomer' | 'byPriceCategory';
  customerId?: string;
  priceCategory?: string;
}

export type ReportIdsForCustomer =
  | C_Report.R3_ADDRESS_LIST_A4_LANDSCAPE
  | C_Report.R4_FACTSHEET
  | C_Report.R5_PHONES_LIST
  | C_Report.R7_WEEKLY_ORDERING_LIST
  | C_Report.R33_PRICE_LIST_FOR_CUSTOMER
  | C_Report.R28_WEEKLY_ORDERING_LIST_BLANK_QUANTITY
  | C_Report.R47_WEEKLY_ORDERINGLIST_BLANK_QUANTITY_BLANK_DATE;
export interface ICustomersReportParams {
  reportId: ReportIdsForCustomer;
  customersIds: Array<string>;
  customerId?: string;
  doIncludeAllArticles?: boolean;
  periodStart?: Date;
}

export type ReportIdsForOffer = C_Report.R37_OFFER_A4;
export interface IOfferReportParams {
  reportId: ReportIdsForOffer;
  offerId: string;
}

export type ReportIdsForOrder =
  | C_Report.R8_DELIVERY_NOTE_A4_WITH_PRICE
  | C_Report.R29_DELIVERY_NOTE_A5_LANDSCAPE_WITH_PRICE;
export interface IOrderReportParams {
  reportId: ReportIdsForOrder;
  orderId: string;
}

export type ReportIdsForInvoice =
  | C_Report.R9_INVOICE_A4
  | C_Report.R59_INVOICE_PAYMENTSLIP_WITH_QR
  | C_Report.R21_INVOICE_JOURNAL;
export interface IInvoiceReportParams {
  reportId: ReportIdsForInvoice;
  invoiceId?: string;
  invoicesIds?: Array<string>;
}

export type ReportIdsForDebtReminder =
  | C_Report.R10_CUSTOMERS_DEBT_NOTIFIER
  | C_Report.R64_CUSTOMERS_DEBT_NOTIFIER_WITH_INVOICE_AND_PAYMENTSLIP;
export interface IDebtReminderReportParams {
  reportId: ReportIdsForDebtReminder;
  invoiceId: string;
  invoiceDate: Date;
  customerId: string;
}

export type ReportIdsForPaymentJournal =
  | C_Report.R24_PAYMENT_JOURNAL_A4_LANDSCAPE
  | C_Report.R35_EXTENDED_PAYMENT_JOURNAL;
export interface IPaymentJournalReportParams {
  reportId: ReportIdsForPaymentJournal;
  paymentsId: string[];
}

type GetR10NotifierRequestRes = NonNullable<
  NonNullable<
    Create_R10_CustomersDebtNotifierMutation['wawiAssist']
  >['create_r10_CustomersDebtNotifier']
>;
type GetR23DebitorJournalA4LandscapeFileRes = NonNullable<
  NonNullable<
    Create_R23_DebitorJournalA4LandscapeMutation['wawiAssist']
  >['create_r23_DebitorJournalA4Landscape']
>;
export interface IGetR10NotifierFileRes extends Omit<GetR10NotifierRequestRes, 'url' | 's3Key'> {
  url: string | null;
  s3Key: string | null;
  /**
   * The report task ended in `WRTS15_NO_DATA_FOR_REPORT`: there was nothing to remind, so no
   * PDF exists and `url`/`s3Key` are null *legitimately* (WA-3868). Absent — not `false` — for
   * the other non-READY statuses, which are real failures wearing the same null-file shape.
   */
  noData?: boolean;
}
export interface IGetR23DebitorJournalA4LandscapeFileRes
  extends Omit<GetR23DebitorJournalA4LandscapeFileRes, 'url' | 's3Key'> {
  url: string | null;
  s3Key: string | null;
}
export type IGetMultipleR10NotifierFilesRes = Array<
  { customerId: string } & IGetR10NotifierFileRes
>;

interface IGetR64NotifierFilesParams {
  r10NotifierParams: Create_R10_CustomersDebtNotifierMutationVariables;
  listPdfHistoryReportParams: ListWa_PdfHistoryReportsQueryVariables;
}
export interface IGetR64NotifierFilesRes {
  notifierPdfUrl: string;
  notifierPdfS3Key: string;
  invoicesPdfs: ListWAPdfHistoryReportRes['reports'];
}
export interface IGetMultipleR64NotifierFilesParams {
  [customerId: string]: {
    r10NotifierParams: Create_R10_CustomersDebtNotifierMutationVariables;
    invoiceIds: Array<string>;
  };
}
export type IGetMultipleR64NotifierFilesRes = Array<
  { customerId: string } & IGetR64NotifierFilesRes
>;

/**
 * The multi-customer notifier subs return either R10 (reminder PDF only) or R64
 * (reminder PDF + the customer's invoice PDFs); callers tell the two apart by probing
 * the first element for `invoicesPdfs`. That probe must survive an EMPTY array:
 * `responseHandler` turns a failed request into a value, so the popups receive their
 * `errorReturnType` sentinel (`[]`) on the success path and `data[0]` is then
 * `undefined` — which is exactly the WA-3801 crash (Sentry WAWIASSIST-247).
 */
export const isMultipleR64NotifierFiles = (
  data: IGetMultipleR10NotifierFilesRes | IGetMultipleR64NotifierFilesRes,
): data is IGetMultipleR64NotifierFilesRes => data.length > 0 && 'invoicesPdfs' in data[0];

/**
 * The customers a batch was requested for that came back WITHOUT a notifier file — the
 * ones whose report task ended in `WRTS15_NO_DATA_FOR_REPORT` (see `hasReportData`).
 * Callers name them to the user and leave them out of the merged preview / the e-mail
 * recipients; an empty file list means nobody had anything to remind.
 */
export const getCustomersWithoutNotifierFile = (
  requestedCustomerIds: ReadonlyArray<string>,
  files: ReadonlyArray<{ customerId: string }>,
): string[] => {
  const withFile = new Set(files.map(({ customerId }) => customerId));
  return requestedCustomerIds.filter((customerId) => !withFile.has(customerId));
};

type GetWinReportsStatusRes = NonNullable<
  NonNullable<GetTasksStatusQuery['wawiAssist']>['getWinReportTasksStatuses']
>;

export type WindowsGeneratedReportPreviewRes = NonNullable<
  NonNullable<Create_R6_ArticleListReportMutation['wawiAssist']>['create_r6_ArticleListReport'] & {
    status: C_Win_Report_Task_Status;
  }
>;
